import instance from '@apis/instance';
import WebAnalytics from '@components/base/web-analytics';
import {
  ChakraProvider,
  CSSReset,
  extendTheme,
  type ThemeConfig,
} from '@chakra-ui/react';
import { ErrorBoundary } from '@components/error/error-boundry';
import AuthGuard from '@components/guards/auth-guard';
import InitComponent from '@components/base/initial-setting';
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { SessionProvider } from 'next-auth/react';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { routeChangeCompleteHandler } from 'utils/common';
import '../public/static/fonts/style.css';
import '../i18n';
import '../styles/globals.scss';
import PopupContainer from '@containers/popup';
import { store } from '@stores/store';

const CHAKRA_CONFIG: ThemeConfig = {
  useSystemColorMode: false,
  initialColorMode: 'light',
};

NProgress.configure({
  showSpinner: false,
});

let PREVIOUS_PATH_NAME = '';

export default function App({ Component, ...rest }: AppProps) {
  const [sessionRefetchInterval, setSessionRefetchInterval] =
    useState<number>(60);
  const router = useRouter();
  const { i18n } = useTranslation();

  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            suspense: true,
          },
        },
      }),
  );
  const activeLabelStyles = {
    transform: 'scale(0.85) translateY(-24px)',
  };
  const theme = extendTheme({
    CHAKRA_CONFIG,
    components: {
      Input: { defaultProps: { focusBorderColor: '#f48fb1' } },
      Textarea: { defaultProps: { focusBorderColor: '#f48fb1' } },
      Modal: {
        sizes: {
          '7xl': { dialog: { maxW: '1500px' } },
        },
      },
      Form: {
        variants: {
          floating: {
            container: {
              _focusWithin: {
                label: {
                  ...activeLabelStyles,
                },
              },
              'input:not(:placeholder-shown) + label, .chakra-select__wrapper + label, textarea:not(:placeholder-shown) ~ label':
                {
                  ...activeLabelStyles,
                },
              label: {
                top: 0,
                left: 0,
                zIndex: 2,
                position: 'absolute',
                pointerEvents: 'none',
                mx: 3,
                px: 1,
                my: 2,
                transformOrigin: 'left top',
              },
            },
          },
        },
      },
    },
  });

  useEffect(() => {
    i18n.changeLanguage(localStorage.getItem('lang') || 'th');
    instance.defaults.headers['lang'] = i18n.language;

    const handleRouteChange = (url: string) => {
      if (url) {
        const [pathname] = url.split('?');

        if (pathname !== PREVIOUS_PATH_NAME) {
          NProgress.start();
          PREVIOUS_PATH_NAME = pathname;
        }
      }
    };
    const handleRouteComplete = (e: any) => {
      NProgress.done();
      const scrollPosition = sessionStorage.getItem(
        `__scroll_${window.history.state.key}`,
      );
      if (scrollPosition) {
        if (window.scrollY === 0) {
          routeChangeCompleteHandler();
        }
        sessionStorage.removeItem(`__scroll_${window.history.state.key}`);
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);
    router.events.on('routeChangeComplete', handleRouteComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('routeChangeComplete', handleRouteComplete);
    };
  }, []);

  useEffect(() => {
    function setScreenSize() {
      let vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
    if (window) {
      setScreenSize();
      window.addEventListener('resize', setScreenSize);
      return () => {
        window.removeEventListener('resize', setScreenSize);
      };
    }
  }, []);

  useEffect(() => {
    if (
      'scrollRestoration' in history &&
      history.scrollRestoration !== 'manual'
    ) {
      history.scrollRestoration = 'manual';
    }
    window.addEventListener('popstate', routeChangeCompleteHandler);
    return () =>
      window.removeEventListener('popstate', routeChangeCompleteHandler);
  }, []);

  if (rest.pageProps.error && Object.keys(rest.pageProps.error).length > 0) {
    if (rest.pageProps.error.status && +rest.pageProps?.error?.status !== 401) {
      return <div>CODE: {rest.pageProps.error.status} Error발생!!!</div>;
    }
  }

  return (
    <>
      <Head>
        <meta name="theme-color" content="#ffffff" />
        <link
          rel="manifest"
          crossOrigin="use-credentials"
          href="/manifest.json"
        />
        <meta name="viewport" content="width=device-width" />

        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="FoodPanther" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/windows11/LargeTile.scale-100.png"
        />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,user-scalable=no"
        />
        <meta content="website" property="og:type" />
        <meta content="foodpanther.asia" property="og:site_name" />
        <meta content="//app.foodpanther.asia" property="og:url" />
        <meta
          content="<Food Panther> ได้เวลาแล้ว ที่คุณจะสั่งอาหารจานโปรดจากร้านเด็ด ส่งตรงถึงบ้านคุณง่ายๆ ในไม่กี่คลิก"
          property="og:description"
        />
        <meta
          content="https://foodpanther-public.s3.ap-northeast-2.amazonaws.com/%ED%91%B8%EB%93%9C%ED%8C%AC%EC%84%9C+%EB%88%84%EB%81%BC.png"
          property="og:image"
        />
        <link
          rel="apple-touch-startup-image"
          media="screen and (device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2) and (orientation: portrait)"
          href="/windows11/LargeTile.scale-100.png"
        />
        <meta
          name="apple-mobile-web-app-status-bar-style"
          content="black-translucent"
        />
        <meta name="theme-color" content="#e91e63" />
      </Head>
      <SessionProvider
        session={rest.pageProps.session}
        refetchInterval={sessionRefetchInterval}
      >
        <Provider store={store}>
          <WebAnalytics />
          <ChakraProvider resetCSS={true} theme={theme}>
            <QueryClientProvider client={client}>
              <Hydrate state={rest.pageProps?.dehydratedState}>
                <CSSReset />
                <ErrorBoundary>
                  <InitComponent error={rest.pageProps?.error}>
                    <AuthGuard
                      setSessionRefetchInterval={setSessionRefetchInterval}
                      error={rest.pageProps?.error}
                    >
                      <Suspense fallback={<div className="min-h-screen"></div>}>
                        <Component {...rest.pageProps}>
                          <PopupContainer />
                        </Component>
                      </Suspense>
                    </AuthGuard>
                  </InitComponent>
                </ErrorBoundary>
              </Hydrate>
            </QueryClientProvider>
          </ChakraProvider>
        </Provider>
      </SessionProvider>
    </>
  );
}
